import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { FileArchive, Upload, KeyRound, LogOut, CheckCircle2, Calendar } from "lucide-react";

/**
 * `/affiliate/paperwork` — Weekly-paperwork dropbox for APPROVED affiliates.
 * Auth = email + paperwork_code combo (no user account needed).
 */
export default function AffiliatePaperwork() {
  const { branding } = useBranding();
  const [creds, setCreds] = useState({ email: "", code: "" });
  const [affiliate, setAffiliate] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [busy, setBusy] = useState(false);

  const [weekOf, setWeekOf] = useState(mondayOf(new Date()));
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);

  const signIn = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/affiliate/paperwork/verify", creds);
      setAffiliate(r.data);
      const list = await api.get(`/affiliate/paperwork/list?email=${encodeURIComponent(creds.email)}&code=${encodeURIComponent(creds.code)}`);
      setUploads(list.data.uploads || []);
      toast.success(`Welcome back, ${r.data.full_name.split(" ")[0]}!`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign-in failed");
    } finally { setBusy(false); }
  };

  const signOut = () => {
    setAffiliate(null);
    setUploads([]);
    setCreds({ email: "", code: "" });
  };

  const uploadFiles = async (filelist) => {
    if (!filelist?.length) return;
    setUploading(true);
    const added = [];
    for (const file of Array.from(filelist)) {
      if (file.size > 20 * 1024 * 1024) { toast.error(`${file.name}: too large (20 MB max)`); continue; }
      const form = new FormData();
      form.append("file", file);
      try {
        const q = new URLSearchParams({ email: creds.email, code: creds.code, week_of: weekOf, note });
        const r = await api.post(`/affiliate/paperwork/upload?${q.toString()}`, form, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ id: r.data.id, filename: r.data.filename });
      } catch (e) {
        toast.error(`${file.name}: ${e?.response?.data?.detail || "upload failed"}`);
      }
    }
    if (added.length) {
      toast.success(`${added.length} file${added.length === 1 ? "" : "s"} uploaded`);
      // Refresh list
      const list = await api.get(`/affiliate/paperwork/list?email=${encodeURIComponent(creds.email)}&code=${encodeURIComponent(creds.code)}`);
      setUploads(list.data.uploads || []);
    }
    setUploading(false);
  };

  return (
    <div className="min-h-screen bg-background paper-texture" data-testid="paperwork-page">
      <header className="border-b border-border bg-card">
        <div className="max-w-3xl mx-auto p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            {branding.logo_url ? <img src={branding.logo_url} alt="" className="h-9 w-9 object-contain" /> :
              <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-serif">{branding.brand_name?.[0]}</div>}
            <div className="font-serif text-2xl">{branding.brand_name}</div>
          </Link>
          <div className="label-caps hidden sm:block">Weekly paperwork · 2026</div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-6 lg:p-12 space-y-8">
        {!affiliate ? (
          <>
            <div>
              <div className="label-caps mb-3 text-primary">Affiliate paperwork dropbox</div>
              <h1 className="font-serif text-4xl lg:text-5xl leading-none mb-3">Drop your weekly docs.</h1>
              <p className="text-muted-foreground">
                Sign in with the email and paperwork code Di'Meka sent when she approved your contract.
              </p>
            </div>
            <form onSubmit={signIn} className="space-y-4 max-w-md" data-testid="paperwork-signin-form">
              <div>
                <Label className="label-caps text-[10px]">Email</Label>
                <Input required type="email" value={creds.email} onChange={e => setCreds({ ...creds, email: e.target.value })} data-testid="paperwork-email" />
              </div>
              <div>
                <Label className="label-caps text-[10px]">Paperwork code</Label>
                <Input required value={creds.code} onChange={e => setCreds({ ...creds, code: e.target.value.toUpperCase() })} className="font-mono tracking-widest" placeholder="XXXXXXXX" data-testid="paperwork-code" />
              </div>
              <Button type="submit" size="lg" disabled={busy} data-testid="paperwork-signin-btn">
                <KeyRound size={16} className="mr-2" /> {busy ? "Verifying…" : "Sign in"}
              </Button>
            </form>
            <Card className="p-4 text-xs text-muted-foreground max-w-md">
              Not approved yet? <Link to="/affiliate/apply" className="underline text-primary">Sign the contract first →</Link>
            </Card>
          </>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="label-caps mb-1 text-primary">Signed in</div>
                <h1 className="font-serif text-3xl lg:text-4xl">{affiliate.full_name}.</h1>
                <div className="text-xs text-muted-foreground">{creds.email}</div>
              </div>
              <Button variant="outline" size="sm" onClick={signOut} data-testid="paperwork-signout">
                <LogOut size={12} className="mr-1" /> Sign out
              </Button>
            </div>

            <Card className="p-5" data-testid="paperwork-upload-card">
              <div className="flex items-center gap-2 mb-3">
                <FileArchive size={18} className="text-primary" />
                <div className="font-serif text-xl">Upload this week's paperwork</div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mb-4">
                <div>
                  <Label className="label-caps text-[10px]">Week of (Monday)</Label>
                  <Input type="date" value={weekOf} onChange={e => setWeekOf(e.target.value)} data-testid="paperwork-week-of" />
                </div>
                <div>
                  <Label className="label-caps text-[10px]">Note (optional)</Label>
                  <Input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g., 4 returns funded this week" data-testid="paperwork-note" />
                </div>
              </div>
              <label className="block">
                <input type="file" multiple accept="application/pdf,image/*" className="hidden" data-testid="paperwork-file-input" onChange={e => uploadFiles(e.target.files)} />
                <div className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${uploading ? "border-primary bg-primary/5" : "border-border hover:border-primary hover:bg-primary/[0.03] cursor-pointer"}`}>
                  <Upload size={22} className="mx-auto mb-2 text-primary" />
                  <div className="text-sm font-medium mb-1">
                    {uploading ? "Uploading…" : "Drop files or click to choose"}
                  </div>
                  <div className="text-xs text-muted-foreground">PDF, PNG, JPG, HEIC · 20 MB per file</div>
                </div>
              </label>
            </Card>

            <div>
              <div className="flex items-center gap-2 mb-3">
                <Calendar size={18} className="text-muted-foreground" />
                <div className="font-serif text-xl">History</div>
                <div className="text-xs text-muted-foreground">({uploads.length} file{uploads.length === 1 ? "" : "s"})</div>
              </div>
              {uploads.length === 0 ? (
                <Card className="p-6 text-sm text-muted-foreground text-center">No uploads yet.</Card>
              ) : (
                <div className="space-y-2">
                  {uploads.map(u => (
                    <Card key={u.id} className="p-3 flex items-center gap-3 text-sm" data-testid={`paperwork-row-${u.id}`}>
                      <CheckCircle2 size={16} className="text-primary shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{u.filename}</div>
                        <div className="text-[11px] text-muted-foreground">
                          Week of {u.week_of || "—"} · Uploaded {new Date(u.created_at).toLocaleString()}
                          {u.note ? ` · ${u.note}` : ""}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function mondayOf(d) {
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const m = new Date(d);
  m.setDate(diff);
  return m.toISOString().slice(0, 10);
}
